import React from 'react';
import { Card, Input, Space, Button, Select, message, Divider, Spin } from 'antd';

const UnitConvert: React.FC = () => {
  const [fromUnit, setFromUnit] = React.useState<'ETH' | 'GWEI' | 'WEI'>('ETH');
  const [toUnit, setToUnit] = React.useState<'ETH' | 'GWEI' | 'WEI'>('GWEI');
  const [inputValue, setInputValue] = React.useState('');
  const [result, setResult] = React.useState('');

  // BTC相关状态
  const [btcValue, setBtcValue] = React.useState('');
  const [satsValue, setSatsValue] = React.useState('');
  const [usdValue, setUsdValue] = React.useState('');
  const [btcPrice, setBtcPrice] = React.useState<number>(100000); // 默认10万美元
  const [priceLoading, setPriceLoading] = React.useState(false);
  const [lastUpdated, setLastUpdated] = React.useState<Date | null>(null);
  const [isUpdating, setIsUpdating] = React.useState(false); // 防止循环更新

  const units = [
    { value: 'ETH', label: 'ETH' },
    { value: 'GWEI', label: 'GWEI' },
    { value: 'WEI', label: 'WEI' },
  ];



  // 转换系数 (相对于WEI)
  const conversionRates = {
    ETH: Math.pow(10, 18),
    GWEI: Math.pow(10, 9),
    WEI: 1,
  };

  // BTC价格获取函数
  const fetchBtcPrice = React.useCallback(async () => {
    setPriceLoading(true);
    try {
      const response = await fetch('https://api.coingecko.com/api/v3/simple/price?ids=bitcoin&vs_currencies=usd');
      const data = await response.json();
      
      if (data.bitcoin && data.bitcoin.usd) {
        setBtcPrice(data.bitcoin.usd);
        setLastUpdated(new Date());
        console.log('BTC价格更新成功:', data.bitcoin.usd);
      } else {
        throw new Error('API数据格式错误');
      }
    } catch (error) {
      console.error('获取BTC价格失败:', error);
      setBtcPrice(100000); // 使用默认价格
      setLastUpdated(new Date());
      message.warning('获取BTC价格失败，使用默认价格 $100,000');
    } finally {
      setPriceLoading(false);
    }
  }, []);

  // 初始化和定时获取BTC价格
  React.useEffect(() => {
    fetchBtcPrice(); // 立即获取一次
    
    const interval = setInterval(() => {
      fetchBtcPrice();
    }, 60000); // 每60秒获取一次

    return () => clearInterval(interval);
  }, [fetchBtcPrice]);

  const handleConvert = () => {
    const value = inputValue.trim();
    if (!value) {
      message.error('请输入数值');
      return;
    }

    const num = parseFloat(value);
    if (isNaN(num) || num < 0) {
      message.error('请输入有效数值');
      return;
    }

    try {
      // 先转换为WEI
      const weiValue = num * conversionRates[fromUnit];
      // 再转换为目标单位
      const convertedValue = weiValue / conversionRates[toUnit];
      
      setResult(convertedValue.toString());
      message.success('转换成功');
    } catch (error) {
      message.error('转换失败');
      setResult('');
    }
  };

  // BTC相关转换函数
  const handleBtcChange = (value: string) => {
    if (isUpdating) return;
    setIsUpdating(true);
    
    setBtcValue(value);
    if (value && !isNaN(parseFloat(value))) {
      const btc = parseFloat(value);
      const sats = btc * 100000000;
      const usd = btc * btcPrice;
      setSatsValue(sats.toString());
      setUsdValue(usd.toFixed(2));
    } else {
      setSatsValue('');
      setUsdValue('');
    }
    
    setTimeout(() => setIsUpdating(false), 0);
  };

  const handleSatsChange = (value: string) => {
    if (isUpdating) return;
    setIsUpdating(true);
    
    setSatsValue(value);
    if (value && !isNaN(parseFloat(value))) {
      const sats = parseFloat(value);
      const btc = sats / 100000000;
      const usd = btc * btcPrice;
      setBtcValue(btc.toString());
      setUsdValue(usd.toFixed(2));
    } else {
      setBtcValue('');
      setUsdValue('');
    }
    
    setTimeout(() => setIsUpdating(false), 0);
  };

  const handleUsdChange = (value: string) => {
    if (isUpdating) return;
    setIsUpdating(true);
    
    setUsdValue(value);
    if (value && !isNaN(parseFloat(value))) {
      const usd = parseFloat(value);
      const btc = usd / btcPrice;
      const sats = btc * 100000000;
      setBtcValue(btc.toString());
      setSatsValue(sats.toString());
    } else {
      setBtcValue('');
      setSatsValue('');
    }
    
    setTimeout(() => setIsUpdating(false), 0);
  };

  const copyToClipboard = () => {
    if (result) {
      navigator.clipboard.writeText(result);
      message.success('已复制到剪贴板');
    }
  };

  const copyBtcToClipboard = (value: string, label: string) => {
    if (value) {
      navigator.clipboard.writeText(value);
      message.success(`已复制${label}到剪贴板`);
    }
  };

  return (
    <Card title="单位转换" className="tool-container-responsive">
      <Space direction="vertical" style={{ width: '100%' }}>
        {/* ETH单位转换 */}
        <div>
          <h3>ETH单位转换</h3>
          <Space style={{ width: '100%', justifyContent: 'center' }}>
            <Select 
              value={fromUnit} 
              onChange={setFromUnit} 
              style={{ width: 100 }}
              options={units}
            />
            <span>转换为</span>
            <Select 
              value={toUnit} 
              onChange={setToUnit} 
              style={{ width: 100 }}
              options={units}
            />
          </Space>
          
          <Input
            placeholder="输入数值"
            value={inputValue}
            onChange={e => setInputValue(e.target.value)}
            onPressEnter={handleConvert}
            addonAfter={fromUnit}
            style={{ marginTop: 8 }}
          />
          
          <div style={{ textAlign: 'center', margin: '8px 0' }}>
            <Button type="primary" onClick={handleConvert}>
              转换
            </Button>
          </div>
          
          <Input
            placeholder="转换结果"
            value={result}
            readOnly
            addonAfter={
              <Space>
                <span>{toUnit}</span>
                <Button 
                  type="text" 
                  size="small" 
                  onClick={copyToClipboard}
                  disabled={!result}
                >
                  复制
                </Button>
              </Space>
            }
          />
        </div>

        <Divider />

        {/* BTC单位转换 */}
        <div>
          <Space align="center">
            <h3>BTC单位转换</h3>
            {priceLoading && <Spin size="small" />}
            <span style={{ fontSize: '12px', color: '#666' }}>
              当前BTC价格: ${btcPrice.toLocaleString()}
              {lastUpdated && ` (${lastUpdated.toLocaleTimeString()})`}
            </span>
          </Space>
          
          <Space direction="vertical" style={{ width: '100%' }}>
            <Input
              placeholder="输入BTC数量"
              value={btcValue}
              onChange={e => handleBtcChange(e.target.value)}
              addonAfter={
                <Space>
                  <span>BTC</span>
                  <Button 
                    type="text" 
                    size="small" 
                    onClick={() => copyBtcToClipboard(btcValue, 'BTC')}
                    disabled={!btcValue}
                  >
                    复制
                  </Button>
                </Space>
              }
            />
            
            <Input
              placeholder="输入SATS数量"
              value={satsValue}
              onChange={e => handleSatsChange(e.target.value)}
              addonAfter={
                <Space>
                  <span>SATS</span>
                  <Button 
                    type="text" 
                    size="small" 
                    onClick={() => copyBtcToClipboard(satsValue, 'SATS')}
                    disabled={!satsValue}
                  >
                    复制
                  </Button>
                </Space>
              }
            />
            
            <Input
              placeholder="输入USD金额"
              value={usdValue}
              onChange={e => handleUsdChange(e.target.value)}
              addonAfter={
                <Space>
                  <span>USD</span>
                  <Button 
                    type="text" 
                    size="small" 
                    onClick={() => copyBtcToClipboard(usdValue, 'USD')}
                    disabled={!usdValue}
                  >
                    复制
                  </Button>
                </Space>
              }
            />
          </Space>
        </div>
      </Space>
    </Card>
  );
};

export default UnitConvert; 